<script setup lang="ts">
// globals
const props = defineProps({
  accept: {
    type: Array,
    default: [] as string[],
  },
  complete: {
    type: Boolean,
    default: false,
  },
  electrack: {
    type: Boolean,
    default: false,
  },
  files: {
    type: Array,
    default: () => [],
  },
  folderId: {
    type: String,
    default: '',
  },
  inboxName: {
    type: String,
    default: 'Inbox',
  },
  limit: {
    type: Number,
    default: 100,
  },
  modelValue: {
    type: [String, Array],
    default: null,
  },
  processing: {
    type: Boolean,
    default: false,
  },
  uploading: {
    type: Boolean,
    default: false,
  },
  useInbox: {
    type: Boolean,
    default: false,
  },
  metadata: {
    type: Object,
    default: () => ({}),
  },
})
const emit = defineEmits([
  'close',
  'update:complete',
  'update:processing',
  'update:files',
  'update:model-value',
  'update:uploading',
])

// static
const theme = {
  tokens: {
    'body-default-font-family': 'roboto, sans-serif',
    'body-default-bold-letter-spacing': '0.0892857143em',
    'surface-surface-brand': 'rgba(var(--v-theme-primary))',
    'surface-surface-brand-hover': 'rgba(var(--v-theme-primary),0.9)',
    'surface-cta-surface-secondary-hover': 'rgb(var(--v-theme-error),0.04)',
    'border-cta-border-secondary': 'rgb(var(--v-theme-error))',
    'border-cta-border-secondary-disabled': 'rgb(var(--v-theme-error))',
    'border-cta-border-secondary-pressed': 'rgb(var(--v-theme-error))',
    'border-cta-border-secondary-hover': 'rgb(var(--v-theme-error))',
    'text-text-on-light': 'rgb(var(--v-theme-error))',
    'text-text-on-light-secondary': 'rgb(var(--v-theme-error))',
  },
}
const types = {
  csv: '.csv,text/csv',
  doc: '.doc,application/msword',
  docx: '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pdf: '.pdf,application/pdf',
  txt: '.txt,text/plain',
  zip: '.zip,application/zip',
  xls: '.xls,application/vnd.ms-excel',
  xlsx: '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
} as any

// composables
const { api } = useFeathers()

// refs
const boxContainer = ref(null)
const isRendered = ref(false)
const input = ref(null as any)
const mabsFile = ref('')
const totalFiles = ref(0)
const uploadButton = ref(null as any)
const uploader = ref(null as any)
defineExpose({ uploadButton })

// computed
const acceptedTypes = computed(() => props.accept?.map((type: any) => types[type]) || [])

// methods
async function onComplete(files: any) {
  const ids = files.map((file: any) => file.id)
  totalFiles.value = files.length
  emit('update:processing', true)
  emit('update:uploading', false)
  emit('update:model-value', props.electrack ? ids[0] : ids)
  await api.service('cc/box').addFileMetadata({
    Files: ids,
    FolderID: props.folderId,
    Metadata: props.metadata,
  })
  await setInput()
  await setUploader()
  emit('update:processing', false)
  emit('update:complete', true)
}
function onMutate(_mutations: any) {
  if (!isRendered.value) {
    setInput()
    setUploader()
    const footerRight = document.querySelector('.bcu-footer-right') as HTMLElement
    if (footerRight) {
      footerRight.children[0].addEventListener('click', () => {
        totalFiles.value = 0
        emit('update:complete', false)
        emit('update:model-value', null)
      })
      uploadButton.value = footerRight.children[1]
      uploadButton.value.addEventListener('click', () => {
        emit('update:uploading', true)
      })
    }
    const footer = document.querySelector('.bcu-footer') as HTMLElement
    if (props.electrack && footer) {
      footer.style.display = 'none'
      footer.style.justifyContent = 'center'
    }
  }
  isRendered.value = true
}
async function setInput() {
  input.value = document.querySelector('[data-testid="upload-input"]') || null
  if (input.value) {
    input.value.accept = acceptedTypes.value.join(',')
    if (props.electrack)
      input.value.removeAttribute('multiple')

    input.value.addEventListener('change', (event: any) => {
      const files = event.target.files
      emit('update:files', files || null)
      if (props.electrack)
        mabsFile.value = files?.[0]?.name || ''
    })
  }
}
async function setUploader() {
  uploader.value = document.querySelector('.bcu-upload-state') as HTMLElement
  if (input.value) {
    uploader.value.onclick = () => {
      input.value.click()
      totalFiles.value = 0
      emit('update:complete', false)
      emit('update:uploading', false)
    }
    uploader.value.style.cursor = 'pointer'
  }
}

// lifecycle
onMounted(async () => {
  // @ts-expect-error: Box is cdn and will throw TS2304 error
  if (window.Box) {
    // @ts-expect-error: Box is cdn and will throw TS2304 error
    const contentUploader = new window.Box.ContentUploader()
    const auth = await api.service('cc/box').authenticate()
    const inbox = await api.service('cc/box').getInbox({ FolderID: props.folderId, Subfolder: props.inboxName })
    const folder = props.useInbox ? inbox?.ID || '' : props.folderId
    contentUploader.addListener('complete', onComplete)
    contentUploader.show(folder, auth?.ID, {
      container: boxContainer.value,
      fileLimit: props.limit,
      onClose: () => emit('close'),
      size: 'small',
      theme,
    })
  }
  else {
    console.error('Box CDN script failed to load.')
  }
})
</script>

<template>
  <v-skeleton-loader
    v-if="!isRendered"
    type="card"
    width="100%"
  />
  <div
    v-show="isRendered"
    id="box-content-uploader"
    ref="boxContainer"
    v-mutate.once="onMutate"
    style="width: 100%"
  />
  <template v-if="isRendered">
    <Teleport to=".bcu-upload-state">
      <div>
        <template v-if="!input">
          <h3 class="primary--text">
            <div>A network error has occurred. Refresh and try again.</div>
            <div>If problem persists, contact site administrator</div>
          </h3>
        </template>
        <template v-else>
          <template v-if="props.electrack && totalFiles > 0">
            <h3 class="primary--text">
              Success!
            </h3>
            <div class="baseText--text pb-4">
              <strong>{{ mabsFile }}</strong> has been uploaded.
            </div>
          </template>
          <template v-else>
            <h3
              v-if="totalFiles > 0"
              class="primary--text pb-2"
            >
              Success! Your {{ totalFiles }} file{{ totalFiles > 1 ? 's' : '' }} have been uploaded.
            </h3>
            <h3 class="primary--text">
              Click to select {{ totalFiles > 0 ? 'more files' : 'a file' }}
            </h3>
            <div class="baseText--text pb-2">
              ...or drag and drop from your desktop
            </div>
          </template>
        </template>
      </div>
    </Teleport>
  </template>
</template>

<style lang="sass">
.be
  font-family: roboto, sans-serif
.bcu
  background-color: rgba(var(--v-theme-surface))
  .bcu-upload-state
    flex-direction: column
  .bcu-footer
    background-color: rgba(var(--v-theme-surface))
  .ReactVirtualized__Table__rowColumn
    text-wrap: balance
  .bcu-upload-state-message, .bcu-upload-input-container
    display: none
</style>
